// STEP T-S1 · TikTok Login Kit 계정 연결 카드 (Production-ready)
// - 기존 TikTok Mock/Test 파이프라인은 백엔드에 그대로 유지 (삭제 금지)
// - 이 카드는 판매자에게 실 TikTok 계정 OAuth 연결 UI 만 제공
// - LIVE 실연동은 별도 STEP 대상 · 카드 문구도 "계정 연결" / "숏폼 연동 준비" 로 한정
import React, { useEffect, useState, useCallback } from 'react';
import { Music, CheckCircle2, Link2, LogOut, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function TikTokAccountCard({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get('/seller/tiktok/status'),
        api.get('/tiktok/config'),
      ]);
      setStatus(s.data || null);
      setConfig(c.data || null);
    } catch { /* silent · unauthenticated */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // URL query 로 콜백 결과 알림
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('tt_connected') === '1') {
      toast.success('TikTok 계정 연결 완료 · 숏폼 연동 준비 완료');
      // clean query
      const u = new URL(window.location.href);
      u.searchParams.delete('tt_connected');
      window.history.replaceState({}, '', u.toString());
      load();
    } else if (p.get('tt_error')) {
      const err = p.get('tt_error');
      const msg = {
        not_configured: 'TikTok API 설정이 아직 완료되지 않았습니다.',
        invalid_state:  'OAuth 상태값이 유효하지 않습니다. 다시 시도해주세요.',
        invalid_response: 'TikTok 응답이 잘못되었습니다.',
        token_exchange_failed: 'TikTok 토큰 교환 실패 · 다시 시도해주세요.',
        save_failed: 'TikTok 계정 저장 실패',
      }[err] || `TikTok 연결 실패: ${err}`;
      toast.error(msg);
      const u = new URL(window.location.href);
      u.searchParams.delete('tt_error');
      window.history.replaceState({}, '', u.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get('/tiktok/oauth/start');
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
      else toast.error('OAuth 시작 URL 을 생성할 수 없습니다.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'TikTok 연결 시작 실패');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('TikTok 계정 연결을 해제하시겠습니까?')) return;
    setBusy(true);
    try {
      await api.post('/seller/tiktok/oauth/disconnect');
      toast.success('TikTok 계정 연결 해제됨');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '연결 해제 실패');
    } finally { setBusy(false); }
  };

  if (!status || !config) return null;
  const oauthReady = !!config.oauth?.configured;
  const connected  = !!status.connected;

  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3 md:p-4"
      data-testid="tiktok-account-card">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-black text-white">
          <Music size={14}/>
        </span>
        <p className="text-sm font-black">TikTok</p>
        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${
          connected ? 'bg-emerald-500/10 text-emerald-700' : 'bg-gray-500/10 text-gray-500'
        }`} data-testid="tiktok-account-status">
          {connected ? '● 계정 연결됨' : '○ 계정 연결 필요'}
        </span>
        {!oauthReady && (
          <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 inline-flex items-center gap-1"
            data-testid="tiktok-not-configured">
            <AlertCircle size={10}/> API 설정 미완료
          </span>
        )}
      </div>

      {connected ? (
        <div className="flex items-center gap-3 flex-wrap">
          {status.avatar_url ? (
            <img src={status.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover"
              data-testid="tiktok-avatar"/>
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/5"/>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black truncate" data-testid="tiktok-display-name">
              {status.display_name || '(이름 미제공)'}
            </p>
            <p className="text-[10px] text-gray-500 inline-flex items-center gap-1">
              <CheckCircle2 size={10} className="text-emerald-500"/>
              TikTok 숏폼 연동 준비 완료 · scope: {(status.scope || '').replace(/,/g, ' · ')}
            </p>
          </div>
          <button onClick={load} disabled={busy}
            className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-gray-400"
            data-testid="tiktok-refresh" title="새로고침">
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''}/>
          </button>
          <button onClick={disconnect} disabled={busy}
            className="px-3 py-1.5 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="tiktok-disconnect">
            <LogOut size={11}/> 연결 해제
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-gray-500 leading-relaxed">
            TikTok 계정을 연결하면 판매자 본인 채널의 공개 숏폼을 LIVEMOA 에 자동으로 가져올 수 있습니다.
            <br/>연결에는 <b>user.info.basic · video.list</b> 권한이 필요합니다.
          </p>
          <button onClick={connect} disabled={busy || !oauthReady}
            className="px-4 py-2 rounded-full text-sm font-black bg-black text-white inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="tiktok-connect">
            <Link2 size={14}/>
            {oauthReady ? 'TikTok 계정 연결' : 'API 설정 미완료 · 관리자 문의'}
          </button>
          {!oauthReady && config.oauth?.notice && (
            <p className="text-[10px] text-amber-700 dark:text-amber-400" data-testid="tiktok-oauth-notice">
              {config.oauth.notice}
            </p>
          )}
        </div>
      )}

      {!compact && (
        <p className="mt-3 text-[10px] text-gray-400 leading-relaxed">
          ※ TikTok LIVE 댓글 실연동은 별도 STEP 에서 진행됩니다. 현재 이 카드는 <b>계정 연결(숏폼 연동 준비)</b> 만 담당합니다.
        </p>
      )}
    </div>
  );
}
